import {
  FlightSegmentViewModel,
  FlightNodeViewModel,
  FlightViewModel,
} from '@api/v3';
import { FLIGHT_CLASS_CODE_NAME_MAP } from '@components/views/flight/constants';
import {
  FlightProps,
  FlightSummaryProps,
  AbroadFlightProps,
} from '@components/views/flight/types';

export const getFlightInfoFromSegment = (
  firstSegment: FlightSegmentViewModel,
  lastSegment: FlightSegmentViewModel,
  numberOfStops: number,
  token: string
): Pick<
  FlightSummaryProps,
  | 'operatingAirline'
  | 'operatingAirlineName'
  | 'operatingAirlineLogo'
  | 'flightClazz'
  | 'flightNo'
  | 'depCode'
  | 'depTime'
  | 'depCityName'
  | 'depDate'
  | 'arrCode'
  | 'arrTime'
  | 'arrDate'
  | 'arrCityName'
  | 'numberOfStops'
  | 'token'
> => {
  return {
    operatingAirline: firstSegment.airline,
    operatingAirlineName: firstSegment.airlineName,
    operatingAirlineLogo: firstSegment.airlineLogo,
    flightClazz: FLIGHT_CLASS_CODE_NAME_MAP[firstSegment.cabinCode],
    flightNo: firstSegment.flightno,
    depCode: firstSegment.depCode,
    depTime: firstSegment.depTime,
    depCityName: firstSegment.depCity,
    depDate: firstSegment.depDate,
    arrCode: lastSegment.arrCode,
    arrTime: lastSegment.arrTime,
    arrDate: lastSegment.arrDate,
    arrCityName: lastSegment.arrCity,
    numberOfStops,
    token,
  };
};

export const getFlightSummary = ({
  durationTime,
  provider,
  providerLogo,
  providerName,
  baggage,
  segments = [],
  token,
}: FlightNodeViewModel & { token: string }): FlightSummaryProps => {
  const numberOfStops = segments.length - 1;
  const partialFlightInfo = getFlightInfoFromSegment(
    segments[0],
    segments[segments.length - 1],
    numberOfStops,
    token
  );

  const flightInfo: FlightSummaryProps = {
    ...partialFlightInfo,
    durationTime,
    providerAirline: provider,
    providerAirlineName: providerName,
    providerAirlineLogo: providerLogo,
    baggage,
  };

  return flightInfo;
};

export const selectFlight = ({
  flights,
  token,
  domestic,
}: Pick<FlightViewModel, 'flights' | 'token' | 'domestic'>): FlightProps => {
  return {
    summary: getFlightSummary({
      ...flights[0],
      token,
    }),
    ...flights[0],
    domestic,
    flights,
  };
};

export const selectAbroadRoundTripFlight = ({
  flights,
  token,
  price,
  domestic,
}: Pick<
  FlightViewModel,
  'flights' | 'token' | 'price' | 'domestic'
>): AbroadFlightProps => {
  const flightsWithSummary = [];
  flightsWithSummary.push({
    summary: getFlightSummary({
      ...flights[0],
      token,
    }),
    ...flights[0],
    flights,
    domestic,
  });
  flights[1] &&
    flightsWithSummary.push({
      summary: getFlightSummary({
        ...flights[1],
        token,
      }),
      ...flights[1],
      flights,
      domestic,
    });

  return { flights: flightsWithSummary, price, token };
};

export const getAbroadFlightsMinimumUnitMilePrice = (
  flights: FlightProps[]
) => {
  return flights.reduce((total, flight) => {
    if (flight && typeof flight.price === 'number') {
      return total + flight.price;
    }
    return total;
  }, 0);
};
